import { apiClient } from "./client";

export type AnnouncementSeverity = "INFO" | "WARNING" | "CRITICAL";

export type AnnouncementRole =
  | "TENANT_ADMIN"
  | "ORG_ADMIN"
  | "COORDINATOR"
  | "DOCTOR"
  | "VIEWER"
  | "AUDITOR";

export const ANNOUNCEMENT_ROLES: AnnouncementRole[] = [
  "TENANT_ADMIN",
  "ORG_ADMIN",
  "COORDINATOR",
  "DOCTOR",
  "VIEWER",
  "AUDITOR",
];

export type Announcement = {
  id: string;
  tenant_id: string;
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  dismissible: boolean;
  starts_at: string;
  ends_at: string | null;
  target_roles: AnnouncementRole[] | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
};

export type AnnouncementCreatePayload = {
  title: string;
  body: string;
  severity?: AnnouncementSeverity;
  dismissible?: boolean;
  starts_at: string;
  ends_at?: string | null;
  target_roles?: AnnouncementRole[] | null;
};

export type AnnouncementPatchPayload = Partial<AnnouncementCreatePayload>;

export async function listActiveAnnouncements(): Promise<Announcement[]> {
  const { data } = await apiClient.get<Announcement[]>("/api/v1/me/announcements");
  return data;
}

export async function listAllAnnouncements(): Promise<Announcement[]> {
  const { data } = await apiClient.get<Announcement[]>("/api/v1/announcements");
  return data;
}

export async function createAnnouncement(
  p: AnnouncementCreatePayload,
): Promise<Announcement> {
  const { data } = await apiClient.post<Announcement>("/api/v1/announcements", p);
  return data;
}

export async function patchAnnouncement(
  id: string,
  p: AnnouncementPatchPayload,
): Promise<Announcement> {
  const { data } = await apiClient.patch<Announcement>(
    `/api/v1/announcements/${id}`,
    p,
  );
  return data;
}

export async function deleteAnnouncement(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/announcements/${id}`);
}

export type AnnouncementAck = {
  announcement_id: string;
  read_at: string;
};

export async function ackAnnouncement(id: string): Promise<AnnouncementAck> {
  const { data } = await apiClient.post<AnnouncementAck>(
    `/api/v1/me/announcements/${id}/ack`,
  );
  return data;
}

export type AnnouncementStats = {
  announcement_id: string;
  seen: number;
  not_seen: number;
  total_users: number;
};

export async function getAnnouncementStats(id: string): Promise<AnnouncementStats> {
  const { data } = await apiClient.get<AnnouncementStats>(
    `/api/v1/announcements/${id}/stats`,
  );
  return data;
}

export type AnnouncementReadRow = {
  user_id: string;
  email: string;
  full_name: string | null;
  read_at: string;
};

export async function listAnnouncementReads(
  id: string,
): Promise<AnnouncementReadRow[]> {
  const { data } = await apiClient.get<AnnouncementReadRow[]>(
    `/api/v1/announcements/${id}/reads`,
  );
  return data;
}
